import {
  keepPreviousData,
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import useApi from "../../api/apiConfig";
import { partialReplaceByWieldyId } from "../../utils/utils";
import {
  type BankTransactionSearchParams,
  findMatchesForTransaction,
  reconcileBankTransactions,
  searchBankTransactions,
  unmatchBankTransaction,
  updateBankTransaction,
  uploadBankTransactions,
} from "./api";

const BANK_TRANSACTIONS_KEY = ["bank-transactions"];
const LIST_KEY = [...BANK_TRANSACTIONS_KEY, "list"];

type TransactionPatch =
  | BankTransactionUpdateMessage
  | (Partial<BankTransactionMessage> & {
      wieldyId: string;
      practiceId: string;
    });

export function useBankTransactions(
  params: BankTransactionSearchParams,
  enabled: boolean,
) {
  const { getClient } = useApi();
  return useQuery({
    queryKey: [...LIST_KEY, params],
    queryFn: async ({ signal }) =>
      searchBankTransactions(await getClient(), params, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function patchBankTransaction(
  queryClient: QueryClient,
  patch: TransactionPatch,
) {
  queryClient.setQueriesData<SearchResponse>({ queryKey: LIST_KEY }, (old) =>
    old ? { ...old, data: partialReplaceByWieldyId(old.data, patch) } : old,
  );
}

export function removeBankTransaction(
  queryClient: QueryClient,
  wieldyId: string,
) {
  queryClient.setQueriesData<SearchResponse>({ queryKey: LIST_KEY }, (old) => {
    if (!old?.data.some((t) => t.wieldyId === wieldyId)) return old;
    return {
      ...old,
      data: old.data.filter((t) => t.wieldyId !== wieldyId),
      total: Math.max(0, old.total - 1),
    };
  });
}

// A settled row can belong to another tab now. Mark every cached list stale so
// those tabs refetch on mount; the active list keeps its patched rows.
export function markBankTransactionListsStale(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: LIST_KEY, refetchType: "none" });
}

function findCachedTransaction(queryClient: QueryClient, wieldyId: string) {
  return queryClient
    .getQueriesData<SearchResponse>({ queryKey: LIST_KEY })
    .flatMap(([, data]) => data?.data ?? [])
    .find((t) => t.wieldyId === wieldyId);
}

interface UpdateBankTransactionVars {
  update: BankTransactionUpdateMessage;
  patch?: BankTransactionUpdateMessage;
  optimistic?: boolean;
}

// The write is eventually consistent, so a refetch right after it can return
// the old row. The pending-mutation poll settles the row instead of invalidating
// the list here.
export function useUpdateBankTransaction() {
  const { getClient } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ update }: UpdateBankTransactionVars) =>
      updateBankTransaction(await getClient(), update),
    onMutate: async ({ update, patch, optimistic }) => {
      if (!optimistic) return { original: undefined };
      await queryClient.cancelQueries({ queryKey: LIST_KEY });
      const original = findCachedTransaction(queryClient, update.wieldyId);
      patchBankTransaction(queryClient, patch ?? update);
      return { original };
    },
    onSuccess: (_data, { update, patch, optimistic }) => {
      if (!optimistic) patchBankTransaction(queryClient, patch ?? update);
    },
    onError: (_error, { update }, context) => {
      if (context?.original) {
        patchBankTransaction(queryClient, {
          ...context.original,
          wieldyId: update.wieldyId,
          practiceId: update.practiceId,
        });
      }
    },
  });
}

export function useUnmatchBankTransaction() {
  const { getClient } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      wieldyId,
    }: {
      wieldyId: string;
      practiceId: string;
    }) => unmatchBankTransaction(await getClient(), wieldyId),
    onSuccess: (_data, { wieldyId, practiceId }) => {
      patchBankTransaction(queryClient, {
        wieldyId,
        practiceId,
        paymentKey: null,
        status: null,
      });
    },
  });
}

export function useFindMatches() {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async (wieldyId: string) =>
      findMatchesForTransaction(await getClient(), wieldyId),
  });
}

export function useUploadBankTransactions() {
  const { getClient } = useApi();
  return useMutation({
    mutationFn: async ({
      practiceId,
      file,
    }: {
      practiceId: string;
      file: File;
    }) => uploadBankTransactions(await getClient(), practiceId, file),
  });
}

export function useReconcileBankTransactions() {
  const { getClient } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      practiceId,
      transactions,
      csvFileId,
    }: {
      practiceId: string;
      transactions: BankTransactionMessage[];
      csvFileId?: string;
    }) =>
      reconcileBankTransactions(
        await getClient(),
        practiceId,
        transactions,
        csvFileId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: BANK_TRANSACTIONS_KEY });
    },
  });
}
